import * as Api from '../Services/Api';

const VISITOR_KEY = 'visitorId';
const CONSENT_KEY = 'gpsConsent';
const SESSION_KEY = 'visitorSessionStarted';

const randomId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `v-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

export const getVisitorId = () => {
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = randomId();
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch (_err) {
    return null;
  }
};

// 'granted' | 'denied' | null (never asked)
export const getGpsConsent = () => {
  try {
    const value = localStorage.getItem(CONSENT_KEY);
    return value === 'granted' || value === 'denied' ? value : null;
  } catch (_err) {
    return null;
  }
};

export const setGpsConsent = (value) => {
  try {
    localStorage.setItem(CONSENT_KEY, value);
  } catch (_err) {
    // ignore
  }
};

export const OPEN_CONSENT_EVENT = 'alcm:open-geo-consent';

// Forget the stored decision and show the consent card again (link in the privacy notice).
export const reopenGpsConsent = () => {
  try {
    localStorage.removeItem(CONSENT_KEY);
  } catch (_err) {
    // ignore
  }
  window.dispatchEvent(new Event(OPEN_CONSENT_EVENT));
};

// Geolocation only exists on https (or localhost).
export const canUseGeolocation = () =>
  typeof window !== 'undefined' && window.isSecureContext && 'geolocation' in navigator;

const isNewSession = () => {
  try {
    if (sessionStorage.getItem(SESSION_KEY)) return false;
    sessionStorage.setItem(SESSION_KEY, '1');
    return true;
  } catch (_err) {
    return false;
  }
};

export const sendHeartbeat = (extra = {}) => {
  const visitorId = getVisitorId();
  if (!visitorId || !Api.sendVisitorHeartbeat) return Promise.resolve();
  const consent = getGpsConsent();
  return Api.sendVisitorHeartbeat({
    visitorId,
    path: window.location.pathname,
    newSession: isNewSession(),
    touch: navigator.maxTouchPoints > 1,
    ...(consent ? { gpsConsent: consent } : {}),
    ...extra,
  }).catch(() => {});
};

const readCoords = () => new Promise((resolve, reject) => {
  navigator.geolocation.getCurrentPosition(
    (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
    reject,
    { enableHighAccuracy: false, timeout: 15000, maximumAge: 5 * 60 * 1000 }
  );
});

// City where the visitor actually is, looked up from their own browser (OpenStreetMap Nominatim).
// Returns null on failure: the server then tries the same lookup itself.
const placeCache = new Map();
const lookupPlace = async ({ lat, lng }) => {
  const key = `${lat.toFixed(3)},${lng.toFixed(3)}`;
  if (placeCache.has(key)) return placeCache.get(key);
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 6000);
    const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&zoom=10&addressdetails=1&accept-language=fr&lat=${lat}&lon=${lng}`;
    const res = await fetch(url, { signal: controller.signal });
    clearTimeout(timer);
    if (!res.ok) return null;
    const a = (await res.json())?.address || {};
    const cityProvince = a.municipality && a.municipality === a.state ? a.municipality : null;
    const city = cityProvince || a.city || a.town || a.village || a.municipality || a.city_district || a.county || null;
    if (!city && !a.country) return null;
    const place = {
      city,
      region: a.state || a.region || null,
      country: a.country || null,
      countryCode: a.country_code ? a.country_code.toUpperCase() : null,
    };
    placeCache.set(key, place);
    return place;
  } catch (_err) {
    return null;
  }
};

export const readPosition = async () => {
  const gps = await readCoords();
  const place = await lookupPlace(gps);
  return place ? { ...gps, place } : gps;
};

export const GPS_REFRESH_MS = 10 * 60 * 1000;

// With consent already given, refresh the position (on load, then every GPS_REFRESH_MS), but only
// when the browser still has the permission (never trigger a new browser prompt).
export const refreshGrantedPosition = async () => {
  if (getGpsConsent() !== 'granted' || !canUseGeolocation() || !navigator.permissions?.query) return;
  try {
    const status = await navigator.permissions.query({ name: 'geolocation' });
    if (status.state !== 'granted') return;
    const gps = await readPosition();
    await sendHeartbeat({ gpsConsent: 'granted', gps });
  } catch (_err) {
    // ignore
  }
};
